import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Header from "../../component/Header";
import Sidebar from "../../component/Sidebar";
import { Oval } from "react-loader-spinner";
import {
  FiPlus, FiTrash2, FiCalendar,
  FiPrinter,
  FiArrowUpCircle, FiArrowDownCircle, FiBarChart2, FiRefreshCw, FiX
} from "react-icons/fi";

import cbeLogo from "../../images/banks/cbe.jpg";
import boaLogo from "../../images/banks/boa.jpg";
import telebirrLogo from "../../images/banks/telebirr.jpg";
import awashLogo from "../../images/banks/awash.jpg";

const apiUrl = process.env.REACT_APP_API_URL;

const BANKS = [
  {
    id: "CBE 2357",
    name: "CBE Yeabkal (...2357)",
    fullName: "Commercial Bank of Ethiopia (Acc: ...2357)",
    accountNumber: "...2357",
    color: "#800080",
    lightColor: "#d6eaf8",
    logo: cbeLogo,
  },
  {
    id: "CBE 1277",
    name: "CBE Gelila (...1277)",
    fullName: "Commercial Bank of Ethiopia (Acc: ...1277)",
    accountNumber: "...1277",
    color: "#710194",
    lightColor: "#ebf5fb",
    logo: cbeLogo,
  },
  {
    id: "CBE 4007",
    name: "CBE Yeabtsega (...4007)",
    fullName: "Commercial Bank of Ethiopia (Acc: ...4007)",
    accountNumber: "...4007",
    color: "#610194",
    lightColor: "#ebf5fb",
    logo: cbeLogo,
  },
  {
    id: "BOA",
    name: "BOA",
    fullName: "Bank of Abyssinia",
    color: "#F1B436",
    lightColor: "#d5f5e3",
    logo: boaLogo,
  },
  {
    id: "Telebirr",
    name: "Telebirr",
    fullName: "Ethio Telecom – Telebirr",
    color: "#248348",
    lightColor: "#e8daef",
    logo: telebirrLogo,
  },
  {
    id: "Awash",
    name: "Awash",
    fullName: "Awash Bank",
    color: "#283691",
    lightColor: "#fadbd8",
    logo: awashLogo,
  },
];

// Cash card definition (separate from BANKS so bank-only logic stays intact)
const CASH_CARD = {
  id: "Cash",
  name: "Cash",
  fullName: "Cash",
  color: "#b45309",
  lightColor: "#fef3c7",
  logo: null,
};

// Combined list for card rendering
const ALL_CARDS = [...BANKS, CASH_CARD];

const MONTHS = [
  { v: "1", n: "January" }, { v: "2", n: "February" }, { v: "3", n: "March" },
  { v: "4", n: "April" }, { v: "5", n: "May" }, { v: "6", n: "June" },
  { v: "7", n: "July" }, { v: "8", n: "August" }, { v: "9", n: "September" },
  { v: "10", n: "October" }, { v: "11", n: "November" }, { v: "12", n: "December" },
];

const TX_TYPES = ["Deposit", "Withdrawal"];
const CASH_TX_TYPES = ["Cash In", "Cash Out", "Bank Deposit"];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function formatNum(n) {
  return parseFloat(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function BankTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [cashTransactions, setCashTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeBank, setActiveBank] = useState("CBE 2357");
  const [view, setView] = useState("entry");

  const [filterMonth, setFilterMonth] = useState((new Date().getMonth() + 1).toString());
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const [form, setForm] = useState({
    bank: "CBE 2357",
    type: "Deposit",
    amount: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    target_bank: "",
  });

  const printRef = useRef(null);

  const isCashActive = activeBank === "Cash";

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const [bankRes, cashRes] = await Promise.all([
        axios.get(`${apiUrl}/api/bank-transactions`),
        axios.get(`${apiUrl}/api/cash-transactions`),
      ]);
      setTransactions(bankRes.data);
      setCashTransactions(cashRes.data);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description || !form.description.trim()) {
      setError("Transaction description is required.");
      return;
    }
    if (!form.amount || !form.date) return;

    try {
      setLoading(true);

      if (isCashActive) {
        if (form.type === "Bank Deposit" && !form.target_bank) {
          setError("Please select which bank to deposit to.");
          setLoading(false);
          return;
        }
        await axios.post(`${apiUrl}/api/cash-transactions`, {
          type: form.type,
          amount: form.amount,
          description: form.description,
          target_bank: form.type === "Bank Deposit" ? form.target_bank : null,
          date: form.date,
        });
      } else {
        await axios.post(`${apiUrl}/api/bank-transactions`, {
          bank: form.bank,
          type: form.type,
          amount: form.amount,
          description: form.description,
          date: form.date,
        });
      }

      setForm((f) => ({ ...f, amount: "", description: "" }));
      await fetchTransactions();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to add transaction");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      setLoading(true);
      if (isCashActive) {
        await axios.delete(`${apiUrl}/api/cash-transactions/${id}`);
      } else {
        await axios.delete(`${apiUrl}/api/bank-transactions/${id}`);
      }
      await fetchTransactions();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to delete");
    } finally {
      setLoading(false);
    }
  };

  // ── Bank filtering ──
  const filteredAll = useMemo(() => {
    return transactions.filter((t) => {
      const d = new Date(t.date);
      const mMatch = !filterMonth || d.getMonth() + 1 === parseInt(filterMonth);
      const yMatch = !filterYear || d.getFullYear() === parseInt(filterYear);
      return mMatch && yMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [transactions, filterMonth, filterYear]);

  const bankFiltered = useMemo(
    () => filteredAll.filter((t) => t.bank === activeBank || (activeBank === "CBE 2357" && t.bank === "CBE")),
    [filteredAll, activeBank]
  );

  // ── Cash filtering ──
  const filteredCash = useMemo(() => {
    return cashTransactions.filter((t) => {
      const d = new Date(t.date);
      const mMatch = !filterMonth || d.getMonth() + 1 === parseInt(filterMonth);
      const yMatch = !filterYear || d.getFullYear() === parseInt(filterYear);
      return mMatch && yMatch;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [cashTransactions, filterMonth, filterYear]);

  // ── Stats for bank cards ──
  const bankStats = useMemo(() => {
    const stats = {};
    BANKS.forEach((b) => {
      const rows = filteredAll.filter((t) => t.bank === b.id || (b.id === "CBE 2357" && t.bank === "CBE"));
      const inflow = rows
        .filter((t) => ["Deposit", "Received"].includes(t.type))
        .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
      const outflow = rows
        .filter((t) => !["Deposit", "Received"].includes(t.type))
        .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
      stats[b.id] = { inflow, outflow, net: inflow - outflow, count: rows.length };
    });
    return stats;
  }, [filteredAll]);

  // ── Stats for cash card ──
  const cashStats = useMemo(() => {
    const inflow = filteredCash
      .filter((t) => t.type === "Cash In")
      .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
    const outflow = filteredCash
      .filter((t) => ["Cash Out", "Bank Deposit"].includes(t.type))
      .reduce((s, t) => s + parseFloat(t.amount || 0), 0);
    return { inflow, outflow, net: inflow - outflow, count: filteredCash.length };
  }, [filteredCash]);

  // Combined stats
  const allStats = useMemo(() => ({
    ...bankStats,
    Cash: cashStats,
  }), [bankStats, cashStats]);

  const totalNet = useMemo(
    () => Object.values(allStats).reduce((s, b) => s + b.net, 0),
    [allStats]
  );

  const activeCardInfo = ALL_CARDS.find((b) => b.id === activeBank);
  const activeFiltered = isCashActive ? filteredCash : bankFiltered;
  const activeStats = allStats[activeBank] || { inflow: 0, outflow: 0, net: 0, count: 0 };

  const withBalance = useMemo(() => {
    let balance = 0;
    const sorted = [...activeFiltered].sort((a, b) => new Date(a.date) - new Date(b.date));
    return sorted.map((t) => {
      const isInflow = isCashActive
        ? t.type === "Cash In"
        : ["Deposit", "Received"].includes(t.type);
      balance += isInflow ? parseFloat(t.amount) : -parseFloat(t.amount);
      return { ...t, balance };
    }).reverse();
  }, [activeFiltered, isCashActive]);

  const handlePrint = () => {
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;
    const win = window.open("", "_blank");
    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>HuluOrder ${isCashActive ? "Cash" : "Bank"} Statement – ${activeCardInfo?.name}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 12mm 15mm;
          }
          * { box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            padding: 0;
            margin: 0;
            color: #1e293b;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .statement-container {
            width: 100%;
            max-width: 100%;
          }
          .header-table {
            width: 100%;
            border-bottom: 2px solid #0f172a;
            padding-bottom: 12px;
            margin-bottom: 20px;
          }
          .brand-title {
            font-size: 24px;
            font-weight: 900;
            letter-spacing: -0.5px;
            color: #0f172a;
            margin: 0;
          }
          .brand-sub {
            font-size: 11px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin: 2px 0 0 0;
          }
          .stmt-title {
            font-size: 18px;
            font-weight: 800;
            color: #0f172a;
            text-transform: uppercase;
            text-align: right;
            margin: 0;
          }
          .stmt-meta {
            font-size: 11px;
            color: #475569;
            text-align: right;
            margin-top: 4px;
            line-height: 1.5;
          }
          table.data-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            font-size: 12px;
          }
          table.data-table th {
            background-color: ${activeCardInfo?.color || "#0f172a"};
            color: #ffffff;
            font-weight: 700;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
            padding: 10px 12px;
            border: 1px solid ${activeCardInfo?.color || "#0f172a"};
          }
          table.data-table td {
            padding: 9px 12px;
            border: 1px solid #e2e8f0;
            color: #334155;
          }
          table.data-table tr:nth-child(even) td {
            background-color: #f8fafc;
          }
          .text-right { text-align: right; }
          .text-left { text-align: left; }
          .text-center { text-align: center; }
          .font-bold { font-weight: 700; }
          .font-black { font-weight: 900; }
          .debit { color: #dc2626; font-weight: 600; }
          .credit { color: #16a34a; font-weight: 600; }
          tfoot td {
            background-color: #0f172a !important;
            color: #ffffff !important;
            font-weight: 700;
            padding: 10px 12px;
            border: 1px solid #0f172a;
          }
          .desc-box {
            margin-top: 24px;
            padding: 16px;
            background-color: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            font-size: 11px;
            color: #475569;
            line-height: 1.6;
          }
          .desc-box h4 {
            margin: 0 0 6px 0;
            font-size: 12px;
            font-weight: 700;
            color: #0f172a;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .summary-grid {
            display: flex;
            justify-content: space-between;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px dashed #cbd5e1;
            font-size: 12px;
          }
          .summary-item span.label { color: #64748b; font-weight: 600; }
          .summary-item span.val { font-weight: 800; color: #0f172a; margin-left: 4px; }
          .signature-section {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #64748b;
          }
          .signature-line {
            border-top: 1px solid #cbd5e1;
            width: 200px;
            text-align: center;
            padding-top: 4px;
          }
        </style>
      </head>
      <body>
        <div class="statement-container">
          ${printContent}
        </div>
      </body>
      </html>
    `);
    win.document.close();
    setTimeout(() => {
      win.print();
    }, 250);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 pb-24">
      <Header />
      <Sidebar />

      <main className="pt-24 px-4 md:px-8 max-w-7xl mx-auto space-y-6">

        {/* Page Title + Filter */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Bank &amp; Cash Transactions</h1>
            <p className="text-slate-500 mt-1 text-sm">Track daily deposits, withdrawals, transfers &amp; shop cash across all accounts</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
              <FiCalendar className="text-slate-400 w-4 h-4" />
              <select
                className="text-sm outline-none bg-transparent text-slate-700 font-medium"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
              >
                <option value="">All Months</option>
                {MONTHS.map((m) => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </div>
            <input
              type="number"
              className="w-24 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none shadow-sm"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              placeholder="Year"
            />
            <button
              onClick={() => {
                setFilterMonth((new Date().getMonth() + 1).toString());
                setFilterYear(new Date().getFullYear().toString());
              }}
              className="flex items-center gap-1.5 bg-slate-900 text-white text-sm px-4 py-2 rounded-xl hover:bg-slate-700 transition"
            >
              <FiRefreshCw className="w-3.5 h-3.5" /> Current
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center gap-2">
            <FiX className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><FiX /></button>
          </div>
        )}

        {/* Bank + Cash Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {ALL_CARDS.map((card) => {
            const s = allStats[card.id] || { inflow: 0, outflow: 0, net: 0, count: 0 };
            const isActive = activeBank === card.id;
            const isCash = card.id === "Cash";
            return (
              <button
                key={card.id}
                onClick={() => {
                  setActiveBank(card.id);
                  if (isCash) {
                    setForm((f) => ({ ...f, bank: "Cash", type: "Cash In", target_bank: "" }));
                  } else {
                    setForm((f) => ({ ...f, bank: card.id, type: "Deposit", target_bank: "" }));
                  }
                  setView("entry");
                }}
                className={`relative text-left p-5 rounded-2xl border-2 transition-all duration-200 shadow-sm hover:shadow-md ${isActive ? "border-transparent shadow-lg scale-[1.02]" : "border-slate-100 bg-white hover:border-slate-200"
                  }`}
                style={isActive ? { background: card.color, color: "#fff" } : {}}
              >
                <div className="flex items-center justify-between mb-3">
                  {isCash ? (
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center text-xl shadow-sm border border-slate-100"
                      style={isActive ? { background: "rgba(255,255,255,0.2)" } : { background: "#fef3c7" }}
                    >
                      💵
                    </div>
                  ) : (
                    <img src={card.logo} alt={card.name} className="w-10 h-10 object-contain rounded-xl bg-white p-1 shadow-sm border border-slate-100" />
                  )}
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={isActive
                      ? { background: "rgba(255,255,255,0.25)", color: "#fff" }
                      : { background: card.lightColor, color: card.color }
                    }
                  >
                    {s.count} tx
                  </span>
                </div>
                <p className={`text-xs font-semibold uppercase tracking-widest mb-1 ${isActive ? "text-white/70" : "text-slate-400"}`}>{isCash ? "Cash" : card.name}</p>
                <p className={`text-xl font-black ${isActive ? "text-white" : "text-slate-800"}`}>{formatNum(s.net)}</p>
                <p className={`text-xs mt-0.5 ${isActive ? "text-white/60" : "text-slate-400"}`}>ETB {isCash ? "balance" : "net"}</p>
                <div className={`flex gap-3 mt-3 text-xs ${isActive ? "text-white/80" : "text-slate-500"}`}>
                  <span className={isActive ? "" : "text-green-600"}>▲ {formatNum(s.inflow)}</span>
                  <span className={isActive ? "" : "text-red-500"}>▼ {formatNum(s.outflow)}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Total Net Bar */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-700 rounded-2xl p-5 text-white flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-xl">
              <FiBarChart2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-white/60 font-medium">Total Net Balance — All Banks + Cash</p>
              <p className="text-3xl font-black">{formatNum(totalNet)} <span className="text-base font-normal text-white/50">ETB</span></p>
            </div>
          </div>
          <div className="md:ml-auto flex flex-wrap gap-6 text-sm">
            {ALL_CARDS.map((b) => (
              <div key={b.id} className="text-center">
                <p className="text-white/50 text-xs">{b.id === "Cash" ? "💵 Cash" : b.name}</p>
                <p className={`font-bold ${(allStats[b.id]?.net || 0) >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {formatNum(allStats[b.id]?.net || 0)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Active Bank/Cash Detail Panel */}
        <div className="rounded-3xl overflow-hidden shadow-xl" style={{ border: `2px solid ${activeCardInfo?.color}30` }}>
          {/* Tab Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-6 pt-5 pb-0 gap-4" style={{ background: activeCardInfo?.color }}>
            <div className="flex items-center gap-3 pb-3">
              {isCashActive ? (
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center text-2xl shadow-md border border-white/20">💵</div>
              ) : (
                <img src={activeCardInfo?.logo} alt={activeCardInfo?.name} className="w-12 h-12 object-contain rounded-xl bg-white p-1 shadow-md border border-white/20" />
              )}
              <div>
                <h2 className="text-white font-black text-xl">{activeCardInfo?.fullName}</h2>
                <p className="text-white/60 text-xs">{MONTHS.find(m => m.v === filterMonth)?.n || "All Months"} {filterYear}</p>
              </div>
            </div>
            <div className="flex gap-1 pb-0">
              {[
                { key: "entry", label: "➕ Add Entry" },
                { key: "transactions", label: "📋 Transactions" },
                { key: "statement", label: "📄 Statement" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setView(tab.key)}
                  className={`px-4 py-2 rounded-t-xl text-sm font-semibold transition-all ${view === tab.key ? "bg-slate-50 text-slate-900" : "text-white/70 hover:text-white hover:bg-white/10"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* ADD ENTRY */}
          {view === "entry" && (
            <div className="bg-white p-8">
              <h3 className="text-lg font-bold text-slate-800 mb-6">
                New {isCashActive ? "Cash" : ""} Transaction — {isCashActive ? "Cash Register" : activeCardInfo?.name}
              </h3>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                {/* Bank selector — only for bank mode */}
                {!isCashActive && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Bank</label>
                    <select
                      className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl p-3 outline-none text-sm"
                      value={form.bank}
                      onChange={(e) => { setForm({ ...form, bank: e.target.value }); setActiveBank(e.target.value); }}
                      required
                    >
                      {BANKS.map((b) => <option key={b.id} value={b.id}>{b.name} – {b.fullName}</option>)}
                    </select>
                  </div>
                )}

                {/* Type selector */}
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Type</label>
                  <select
                    className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl p-3 outline-none text-sm"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    required
                  >
                    {(isCashActive ? CASH_TX_TYPES : TX_TYPES).map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>

                {/* Target bank — only for Cash → Bank Deposit */}
                {isCashActive && form.type === "Bank Deposit" && (
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Deposit To Bank</label>
                    <select
                      className="w-full bg-slate-50 border border-slate-200 focus:border-amber-500 rounded-xl p-3 outline-none text-sm"
                      value={form.target_bank}
                      onChange={(e) => setForm({ ...form, target_bank: e.target.value })}
                      required
                    >
                      <option value="">— Select Bank —</option>
                      {BANKS.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Amount (ETB)</label>
                  <input
                    className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl p-3 outline-none text-sm"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Date</label>
                  <input
                    className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl p-3 outline-none text-sm"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description</label>
                  <input
                    className="w-full bg-slate-50 border border-slate-200 focus:border-blue-500 rounded-xl p-3 outline-none text-sm"
                    type="text"
                    placeholder={isCashActive ? "e.g. Customer cash payment" : "Enter transaction description..."}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    required
                  />
                </div>
                <div className="md:col-span-2 lg:col-span-5">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-60"
                    style={{ background: activeCardInfo?.color }}
                  >
                    {loading
                      ? <Oval height={20} width={20} color="#fff" strokeWidth={3} />
                      : <><FiPlus className="w-5 h-5" /> {isCashActive ? "Record Cash Transaction" : "Record Transaction"}</>
                    }
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* TRANSACTIONS LIST */}
          {view === "transactions" && (
            <div className="bg-white">
              <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-slate-500">{activeFiltered.length} transactions</p>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600 font-semibold">
                    <FiArrowUpCircle /> {formatNum(activeStats.inflow)} in
                  </span>
                  <span className="flex items-center gap-1 text-red-500 font-semibold">
                    <FiArrowDownCircle /> {formatNum(activeStats.outflow)} out
                  </span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-slate-50">
                      {["#", "Date", "Type", "Description", "Amount (ETB)", "Actions"].map((h) => (
                        <th key={h} className={`px-6 py-3 text-xs font-bold text-slate-400 uppercase tracking-widest ${h === "Amount (ETB)" ? "text-right" : h === "Actions" ? "text-center" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {activeFiltered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-16 text-center text-slate-400 italic">
                          No transactions for this period. Switch to "Add Entry" to get started.
                        </td>
                      </tr>
                    ) : (
                      activeFiltered.map((t, i) => {
                        const isInflow = isCashActive
                          ? t.type === "Cash In"
                          : ["Deposit", "Received"].includes(t.type);
                        return (
                          <tr key={t.id} className="group hover:bg-slate-50/60 transition-colors">
                            <td className="px-6 py-4 text-slate-300 group-hover:text-slate-600 font-bold">{i + 1}</td>
                            <td className="px-6 py-4 text-slate-600 whitespace-nowrap">{formatDate(t.date)}</td>
                            <td className="px-6 py-4">
                              <span
                                className="px-2.5 py-1 rounded-full text-xs font-bold"
                                style={
                                  t.type === "Bank Deposit"
                                    ? { background: "#dbeafe", color: "#1d4ed8" }
                                    : isInflow
                                      ? { background: "#dcfce7", color: "#16a34a" }
                                      : { background: "#fee2e2", color: "#dc2626" }
                                }
                              >
                                {t.type === "Bank Deposit" ? "🏦" : isInflow ? "▲" : "▼"} {t.type}
                                {t.type === "Bank Deposit" && t.target_bank ? ` → ${t.target_bank}` : ""}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-500 max-w-xs truncate">{t.description || "—"}</td>
                            <td className={`px-6 py-4 text-right font-bold text-base ${isInflow ? "text-green-600" : "text-red-500"}`}>
                              {isInflow ? "+" : "-"}{formatNum(t.amount)}
                            </td>
                            <td className="px-6 py-4 text-center opacity-0 group-hover:opacity-100 transition">
                              <button onClick={() => handleDelete(t.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition" title="Delete">
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {activeFiltered.length > 0 && (
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                      <tr>
                        <td colSpan={4} className="px-6 py-4 text-slate-700">PERIOD TOTAL</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-lg font-black ${activeStats.net >= 0 ? "text-green-700" : "text-red-600"}`}>
                            {formatNum(activeStats.net)} ETB
                          </span>
                        </td>
                        <td />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* STATEMENT */}
          {view === "statement" && (
            <div className="bg-white">
              <div className="px-8 py-5 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">
                    {MONTHS.find(m => m.v === filterMonth)?.n || "All"} {filterYear} — {isCashActive ? "Cash" : "Bank"} Statement
                  </h3>
                  <p className="text-slate-400 text-xs mt-0.5">HuluOrder Financial Statement • {activeCardInfo?.fullName}</p>
                </div>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-slate-900 text-white text-sm px-5 py-2.5 rounded-xl hover:bg-slate-800 shadow-md transition"
                >
                  <FiPrinter className="w-4 h-4" /> Print / Export A4 PDF
                </button>
              </div>

              <div ref={printRef} className="p-8 max-w-4xl mx-auto bg-white border border-slate-200 my-6 shadow-sm rounded-xl">
                {/* Header */}
                <table className="header-table w-full mb-6 pb-4 border-b-2 border-slate-800">
                  <tbody>
                    <tr>
                      <td className="align-top border-none p-0">
                        <div className="flex items-center gap-3">
                          {isCashActive ? (
                            <div className="w-14 h-14 rounded-xl border border-slate-200 p-1 bg-white shadow-sm flex items-center justify-center text-3xl">💵</div>
                          ) : (
                            <img src={activeCardInfo?.logo} alt={activeCardInfo?.name} className="w-14 h-14 object-contain rounded-xl border border-slate-200 p-1 bg-white shadow-sm" />
                          )}
                          <div>
                            <h1 className="brand-title text-2xl font-black text-slate-900 tracking-tight">HULUORDER</h1>
                            <p className="brand-sub text-xs font-bold text-slate-500 uppercase tracking-widest mt-0.5">Financial &amp; {isCashActive ? "Cash" : "Banking"} Statement</p>
                            <p className="text-xs text-slate-600 mt-1">
                              <strong className="text-slate-800">{isCashActive ? "Account:" : "Bank Partner:"}</strong> {activeCardInfo?.fullName} ({isCashActive ? "Cash" : activeCardInfo?.name})
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="align-top text-right border-none p-0">
                        <h2 className="stmt-title text-lg font-black text-slate-900 uppercase">OFFICIAL STATEMENT</h2>
                        <div className="stmt-meta text-xs text-slate-600 mt-1 space-y-0.5">
                          <p><strong className="text-slate-800">Period:</strong> {MONTHS.find(m => m.v === filterMonth)?.n || "All Months"} {filterYear}</p>
                          <p><strong className="text-slate-800">Generated:</strong> {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</p>
                          <p><strong className="text-slate-800">Ref No:</strong> HO-STMT-{activeBank}-{filterYear}{filterMonth ? filterMonth.padStart(2, '0') : 'ALL'}</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Statement Table */}
                <table className="data-table w-full text-xs border-collapse border border-slate-200">
                  <thead>
                    <tr style={{ background: activeCardInfo?.color || "#0f172a", color: "#ffffff" }}>
                      {["Date", "Description / Reference", "Type", "Debit (ETB)", "Credit (ETB)", "Balance (ETB)"].map((h) => (
                        <th key={h} className={`p-3 font-bold text-left uppercase tracking-wider text-[11px] ${["Debit (ETB)", "Credit (ETB)", "Balance (ETB)"].includes(h) ? "text-right" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {withBalance.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-slate-400 italic">No transactions recorded for this period.</td>
                      </tr>
                    ) : (
                      withBalance.map((t, i) => {
                        const isCredit = isCashActive
                          ? t.type === "Cash In"
                          : ["Deposit", "Received"].includes(t.type);
                        return (
                          <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50/70"}>
                            <td className="p-3 text-slate-700 whitespace-nowrap font-medium">{formatDate(t.date)}</td>
                            <td className="p-3 text-slate-800 max-w-xs">
                              {t.description || `${t.type}${t.target_bank ? ` → ${t.target_bank}` : ""}`}
                            </td>
                            <td className="p-3">
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase ${t.type === "Bank Deposit"
                                ? "bg-blue-100 text-blue-800"
                                : isCredit ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                                }`}>
                                {t.type}
                              </span>
                            </td>
                            <td className="p-3 text-right text-red-600 font-semibold">{!isCredit ? formatNum(t.amount) : "—"}</td>
                            <td className="p-3 text-right text-green-600 font-semibold">{isCredit ? formatNum(t.amount) : "—"}</td>
                            <td className={`p-3 text-right font-black ${t.balance >= 0 ? "text-slate-900" : "text-red-600"}`}>{formatNum(t.balance)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {withBalance.length > 0 && (
                    <tfoot>
                      <tr style={{ background: activeCardInfo?.color || "#0f172a", color: "#ffffff" }}>
                        <td colSpan={3} className="p-3 font-bold text-xs">CLOSING STATEMENT TOTALS</td>
                        <td className="p-3 text-right font-bold text-xs">{formatNum(activeStats.outflow)}</td>
                        <td className="p-3 text-right font-bold text-xs">{formatNum(activeStats.inflow)}</td>
                        <td className="p-3 text-right font-black text-sm">{formatNum(activeStats.net)} ETB</td>
                      </tr>
                    </tfoot>
                  )}
                </table>

                {/* Summary Box */}
                <div className="desc-box mt-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                  <h4 className="font-bold text-slate-800 text-xs uppercase tracking-wider mb-1">Statement Summary &amp; Description Notes</h4>
                  <p className="leading-relaxed">
                    This official statement details all recorded financial movements for <strong className="text-slate-800">{activeCardInfo?.fullName} ({isCashActive ? "Cash" : activeCardInfo?.name})</strong> within the period of <strong className="text-slate-800">{MONTHS.find(m => m.v === filterMonth)?.n || "All Months"} {filterYear}</strong>.
                    {isCashActive
                      ? " Cash In represents money received from customers paying in cash. Cash Out represents payments and expenses made from the cash register. Bank Deposits represent cash moved from the register into a bank account."
                      : " Debits represent payments, withdrawals, and outgoing transfers. Credits represent deposits and incoming receipts."
                    }
                  </p>
                  <div className="summary-grid flex justify-between items-center mt-3 pt-3 border-t border-slate-200 text-xs">
                    <div className="summary-item"><span className="label text-slate-500">Total Entries:</span> <span className="val font-bold text-slate-800">{activeFiltered.length}</span></div>
                    <div className="summary-item"><span className="label text-slate-500">Total Credits (+):</span> <span className="val font-bold text-green-700">{formatNum(activeStats.inflow)} ETB</span></div>
                    <div className="summary-item"><span className="label text-slate-500">Total Debits (-):</span> <span className="val font-bold text-red-600">{formatNum(activeStats.outflow)} ETB</span></div>
                    <div className="summary-item"><span className="label text-slate-500">Net Period Balance:</span> <span className="val font-black text-slate-900">{formatNum(activeStats.net)} ETB</span></div>
                  </div>
                </div>

                {/* Signature */}
                <div className="signature-section mt-12 flex justify-between items-end text-xs text-slate-500">
                  <div className="text-left">
                    <p className="font-semibold text-slate-700">HuluOrder Accounting Department</p>
                    <p className="text-[10px] text-slate-400">System-generated document &bull; Verified</p>
                  </div>
                  <div className="signature-line border-t border-slate-300 w-48 text-center pt-1 text-[11px] font-medium text-slate-600">
                    Authorized Signature &amp; Stamp
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
